import React, { Ref, useRef, useState } from 'react';
import { createIconElement, getUUID } from '@/lib';
import styles from './StanInput.module.css';
import { IconType } from 'react-icons/lib';
import EyeShowIcon from '@/assets/icons/ic-eye-show.svg';
import EyeHideIcon from '@/assets/icons/ic-eye-hide.svg';
import { StanIcon } from '../types';

interface StanInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
    name: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    topLabel?: string;
    innerLabel?: string;
    error?: boolean;
    hintText?: string;
    startIcon?: StanIcon;
    endIcon?: IconType;
    ref?: Ref<HTMLInputElement>;
}

export const StanInput = ({
    id = getUUID(),
    name,
    type = 'text',
    topLabel,
    innerLabel,
    value,
    onChange,
    required = false,
    disabled = false,
    readOnly = false,
    className = '',
    hintText,
    startIcon,
    endIcon,
    error = false,
    ref,
    ...restProps
}: StanInputProps) => {
    // Use the forwarded ref directly, or create internal ref if none provided
    const internalRef = useRef<HTMLInputElement>(null);
    const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;
    const [showPassword, setShowPassword] = useState(false);

    const handleWrapperClick = (e: React.MouseEvent) => {
        // Don't focus if clicking directly on the input field - let the input handle its own events
        // Also don't focus if input is disabled or readOnly
        if (!disabled && !readOnly && inputRef.current && e.target !== inputRef.current) {
            inputRef.current.focus();
        }
    };

    const getVariantClass = () => {
        if (disabled) return styles.disabled;
        if (error) return styles.negative;
        return '';
    };

    const containerClasses = `stan-input ${getVariantClass()} ${className}`.trim();

    const StartIcon = startIcon ? createIconElement(startIcon, { className: `${styles.icon} mr-3 size-5` }) : null;
    const EndIcon = endIcon ? createIconElement(endIcon, { className: `${styles.icon} ml-3 size-5` }) : null;
    // Don't show eye icons if there is an end icon
    const EyeShow =
        type === 'password' && !EndIcon
            ? createIconElement(EyeShowIcon, {
                  className: `${styles.icon} ml-3 size-5`,
                  removeFill: true,
                  onClick: () => setShowPassword(false),
              })
            : null;
    const EyeHide =
        type === 'password' && !EndIcon
            ? createIconElement(EyeHideIcon, {
                  className: `${styles.icon} ml-3 size-5`,
                  removeFill: true,
                  onClick: () => setShowPassword(true),
              })
            : null;

    return (
        <div className={containerClasses}>
            {topLabel && (
                <label className={styles.label} style={{ marginBottom: '0.5rem' }}>
                    {topLabel}
                    {required && <span className={styles.required}>*</span>}
                </label>
            )}
            <div
                className={`${styles.wrapper} ${readOnly ? 'cursor-pointer' : 'cursor-text'}`}
                onClick={handleWrapperClick}
            >
                {StartIcon}
                <div className='flex flex-1 flex-col'>
                    {innerLabel && (
                        <label className={`${styles.innerLabel} ${readOnly ? 'cursor-pointer' : 'cursor-text'}`}>
                            {innerLabel}
                        </label>
                    )}

                    <input
                        ref={inputRef}
                        id={id}
                        name={name}
                        type={type === 'password' && showPassword ? 'text' : type}
                        required={required}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                        readOnly={readOnly}
                        className={`${styles.field} ${readOnly ? 'cursor-pointer' : 'cursor-text'}`}
                        {...restProps}
                    />
                </div>

                {EndIcon}
                {showPassword && EyeShow}
                {!showPassword && EyeHide}
            </div>

            {hintText && <span className={styles.hint}>{hintText}</span>}
        </div>
    );
};

StanInput.displayName = 'StanInput';
